import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button, Loading } from '@/components/ui'
import {
  addWhatsAppSubscription,
  cancelWhatsAppSubscription,
  listWhatsAppSubscriptions,
  updateWhatsAppSubscription,
  type CentralSubscription,
} from '@/api/whatsapp'
import SubscriptionForm from './SubscriptionForm'

function statusClass(status: string): string {
  if (status === 'active') return 'bg-success/15 text-success'
  if (status === 'trial') return 'bg-amber-500/15 text-amber-300'
  if (status === 'cancelled') return 'bg-navy-hover text-ink-secondary'
  if (status === 'expired') return 'bg-danger/15 text-danger'
  return 'bg-navy-hover text-ink-secondary'
}

function planClass(plan: string): string {
  if (plan === 'enterprise') return 'bg-indigo-500/20 text-indigo-300'
  if (plan === 'pro') return 'bg-amber-500/20 text-amber-300'
  return 'bg-navy-hover text-ink-secondary'
}

function formatOptionalDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

export default function SubscriptionTable() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'add' | 'edit' | null>(null)
  const [selected, setSelected] = useState<CentralSubscription | null>(null)

  const { data = [], isLoading } = useQuery({
    queryKey: ['wa-subscriptions'],
    queryFn: listWhatsAppSubscriptions,
    staleTime: 30_000,
  })

  const addMutation = useMutation({
    mutationFn: async (payload: Partial<CentralSubscription>) => {
      await addWhatsAppSubscription({
        org_id: payload.org_id ?? '',
        organization_name: payload.organization_name ?? '',
        user_email: payload.user_email ?? '',
        plan: (payload.plan as CentralSubscription['plan']) ?? 'basic',
        status: (payload.status as CentralSubscription['status']) ?? 'active',
        expires_at: payload.expires_at ?? null,
      })
    },
    onSuccess: async () => {
      toast.success('Subscription created.')
      await queryClient.invalidateQueries({ queryKey: ['wa-subscriptions'] })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Failed to add subscription.'
      toast.error(message)
    },
  })

  const editMutation = useMutation({
    mutationFn: async (payload: Partial<CentralSubscription>) => {
      if (!selected?.org_id) throw new Error('Missing org id.')
      await updateWhatsAppSubscription(selected.org_id, {
        organization_name: payload.organization_name ?? selected.organization_name,
        user_email: payload.user_email ?? selected.user_email,
        plan: (payload.plan as CentralSubscription['plan']) ?? selected.plan,
        status: (payload.status as CentralSubscription['status']) ?? selected.status,
        expires_at: payload.expires_at ?? null,
      })
    },
    onSuccess: async () => {
      toast.success('Subscription updated.')
      await queryClient.invalidateQueries({ queryKey: ['wa-subscriptions'] })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Failed to update subscription.'
      toast.error(message)
    },
  })

  const cancelMutation = useMutation({
    mutationFn: cancelWhatsAppSubscription,
    onSuccess: async () => {
      toast.success('Subscription cancelled.')
      await queryClient.invalidateQueries({ queryKey: ['wa-subscriptions'] })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Failed to cancel subscription.'
      toast.error(message)
    },
  })

  const isSaving = addMutation.isPending || editMutation.isPending
  const sortedRows = useMemo(
    () => [...data].sort((a, b) => a.org_id.localeCompare(b.org_id)),
    [data]
  )

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-white">Subscriptions</h2>
        <Button
          size="sm"
          onClick={() => {
            setSelected(null)
            setMode('add')
          }}
        >
          Add Subscription
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading subscriptions..." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-navy-border">
          <table className="min-w-full text-sm bg-navy-card">
            <thead>
              <tr className="bg-navy-hover text-ink-secondary">
                <th className="text-left font-medium px-4 py-3">Org ID</th>
                <th className="text-left font-medium px-4 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Email</th>
                <th className="text-left font-medium px-4 py-3">Plan</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Expires At</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-ink-muted">
                    No WhatsApp subscriptions found.
                  </td>
                </tr>
              )}
              {sortedRows.map((row) => (
                <tr key={row.org_id} className="border-t border-navy-border">
                  <td className="px-4 py-3 font-mono text-indigo-300">{row.org_id}</td>
                  <td className="px-4 py-3 text-ink-primary">{row.organization_name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{row.user_email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${planClass(row.plan)}`}>
                      {row.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${statusClass(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary font-mono text-xs">
                    {formatOptionalDate(row.expires_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSelected(row)
                          setMode('edit')
                        }}
                      >
                        Edit
                      </Button>
                      {row.status === 'active' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-ink-secondary hover:text-danger"
                          loading={cancelMutation.isPending}
                          onClick={async () => {
                            const shouldCancel = window.confirm(`Cancel subscription for ${row.org_id}?`)
                            if (!shouldCancel) return
                            await cancelMutation.mutateAsync(row.org_id)
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode && (
        <SubscriptionForm
          mode={mode}
          initial={mode === 'edit' ? selected ?? undefined : undefined}
          onClose={() => {
            if (!isSaving) {
              setMode(null)
              setSelected(null)
            }
          }}
          onSave={async (payload) => {
            if (mode === 'add') {
              await addMutation.mutateAsync(payload)
              return
            }
            await editMutation.mutateAsync(payload)
          }}
        />
      )}
    </section>
  )
}
